import React, {useState} from 'react';

import {Alert, Button, FieldError, Label} from '@design-system';

import AccountUpdatedDialog from '../Account/AccountUpdatedDialog';
import useRetryFocus from '../Util/useRetryFocus';
import useRetryHold from '../Util/useRetryHold';

// Geometry matches the Input primitive, raised to the 44px minimum target the
// house rule sets for anything the pointer has to hit. Colour resolves through
// the Layer 2 roles only. Same shape as the Professional onboarding form's
// select, which is the exemplar for this pattern.
const selectStyles = [
  'block h-11 w-full rounded-md border px-3 text-sm',
  'border-(--compass-border) bg-(--compass-surface) text-(--compass-on-surface)',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)',
  'aria-[invalid=true]:border-(--compass-border-invalid)',
].join(' ');

/**
 * A reference-data-backed `<select>` that announces its own loading and
 * failure in place. The editors used to replace the entire form with the bare
 * string "Loading", and a failed request threw while reading `undefined`.
 *
 * A failure keeps its Retry, and focus, on screen while the list is asked for
 * again and when it fails again; a Retry that loads the list hands focus to the
 * select, unless the person has moved on. `focusFirstFailure` says whether the
 * failure takes focus when it first shows: always where the list is the
 * editor's only control, and otherwise only when nothing else has focus, since
 * the person may be choosing beside it.
 */
export const ReferenceDataSelect = ({
  id,
  testId,
  label,
  placeholder,
  query,
  options,
  defaultValue = '',
  field,
  invalid,
  errorMessage,
  focusFirstFailure = 'when-unfocused',
}) => {
  const errorId = `${id}-error`;
  const describedBy = errorMessage ? errorId : undefined;
  const {retrying, retryPending, retry, loadedRef} = useRetryHold(query);
  const showFailure = query.data === undefined && Boolean(query.error || retrying);
  const {failureRef} = useRetryFocus({failed: showFailure, focusFirstFailure}, retry);

  if (showFailure) {
    return (
      <div>
        <p className="mb-1 block leading-none font-medium text-(--compass-on-surface)">{label}</p>
        <Alert
          ref={failureRef}
          variant="error"
          data-testid={`${testId}-error`}
          className="mt-2"
          action={
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              data-testid={`${testId}-retry`}
              aria-busy={retryPending ? true : undefined}
              onClick={retry}>
              Retry
            </Button>
          }>
          {`We couldn't load the ${label.toLowerCase()} options. Please try again.`}
        </Alert>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div>
        <p className="mb-1 block leading-none font-medium text-(--compass-on-surface)">{label}</p>
        <p role="status" data-testid={`${testId}-loading`} className="text-sm">
          Loading {label.toLowerCase()} options…
        </p>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={id} className="mb-1 block">
        {label}
      </Label>
      <select
        id={id}
        defaultValue={defaultValue}
        className={selectStyles}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        {...field}
        ref={(element) => {
          field?.ref?.(element);
          loadedRef.current = element;
        }}>
        <option value="">{placeholder}</option>
        {(options ?? []).map((option) => (
          <option key={`${testId}-${option.id}`} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
      <FieldError id={errorId}>{errorMessage}</FieldError>
    </div>
  );
};

/**
 * The shared shell for the three Professional profile editors.
 *
 * The pending state used to replace the whole form with a spinner, so the
 * value being saved disappeared from under the person while the request ran
 * and nothing was announced. The form stays, the control reports `aria-busy`,
 * and the result arrives in the dialog exactly as before.
 */
const ProAccountFieldEdit = ({
  handleSubmit,
  onSubmit,
  children,
  buttonDisabled,
  dialogMessage = '',
}) => {
  const [shouldShowUpdateDialog, setShouldShowUpdateDialog] = useState(false);
  const [updateWasSuccessful, setUpdateWasSuccessful] = useState(undefined);
  const [isUpdating, setIsUpdating] = useState(false);

  return (
    <form
      data-testid="save-region"
      className="mx-auto w-full max-w-[900px] px-4 py-12"
      onSubmit={handleSubmit(async (data) => {
        if (isUpdating) return;
        setIsUpdating(true);
        try {
          const response = await onSubmit(data);
          setUpdateWasSuccessful(Boolean(response && response.status === 200));
        } catch {
          setUpdateWasSuccessful(false);
        }
        setShouldShowUpdateDialog(true);
        setIsUpdating(false);
      })}>
      <AccountUpdatedDialog
        isVisible={shouldShowUpdateDialog}
        closeDialog={() => setShouldShowUpdateDialog(false)}
        updateWasSuccessful={updateWasSuccessful}
        dialogMessage={dialogMessage}
      />
      <div data-testid="save-content" aria-busy={isUpdating}>
        {children}
        {isUpdating && (
          <p role="status" data-testid="pro-account-saving" className="mt-4">
            Saving your changes…
          </p>
        )}
      </div>
      <div className="mt-6 flex justify-end">
        <Button type="submit" className="min-h-11" aria-busy={isUpdating} disabled={buttonDisabled}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default ProAccountFieldEdit;
